import React, { useState, useEffect } from 'react';
import { Button, DatePicker, Table, Spin, message, Modal, Tooltip, InputNumber, Tag, Checkbox, Space, Form, Popconfirm, Select, Input } from 'antd';
import { InfoCircleOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { listUsers } from '../services/userService';
import { getAttendanceForMonth, getAllUsersAttendanceForMonth, updateMonthlyAttendance } from '../services/attendanceService';
import { getMonthlyAdvanceDetails, getAllUsersMonthlyAdvanceDetails } from '../services/advanceService';
import { getSites } from '../services/siteService';
import { generateSalarySlip, getSalarySlip, listSalarySlipsForMonth, updateSalarySlip, deleteSalarySlip, recalculateSalaryAfterAttendanceUpdate } from '../services/salarySlipService';
import { themeConfig } from '../config/theme';
import dayjs from 'dayjs';
import './SalarySlipManagement.css';

const SalarySlipManagement = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedMonth, setSelectedMonth] = useState(dayjs().subtract(1, 'month').startOf('month'));
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [displayLoading, setDisplayLoading] = useState(false);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [advanceHistory, setAdvanceHistory] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [displayedSlips, setDisplayedSlips] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingSlip, setEditingSlip] = useState(null);
  const [editForm] = Form.useForm();
  const [carryForwardInfo, setCarryForwardInfo] = useState({});
  const [bulkAdvanceData, setBulkAdvanceData] = useState([]);
  
  // Material narration modal state
  const [materialNarrationModalVisible, setMaterialNarrationModalVisible] = useState(false);
  const [editingMaterialNarration, setEditingMaterialNarration] = useState(null);
  const [materialNarrationForm] = Form.useForm();
  
  // Bonus narration modal state
  const [bonusNarrationModalVisible, setBonusNarrationModalVisible] = useState(false);
  const [editingBonusNarration, setEditingBonusNarration] = useState(null);
  const [bonusNarrationForm] = Form.useForm();
  
  // Attendance edit modal state
  const [attendanceEditModalVisible, setAttendanceEditModalVisible] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [attendanceEditForm] = Form.useForm();
  const [attendanceData, setAttendanceData] = useState({});
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [searchText, setSearchText] = useState('');


  // Set form values when attendance data changes
  useEffect(() => {
    if (attendanceData && Object.keys(attendanceData).length > 0) {
      console.log('Setting form values with attendance data:', attendanceData);
      attendanceEditForm.setFieldsValue(attendanceData);
    }
  }, [attendanceData, attendanceEditForm]);

  const handleFetchData = async () => {
    setLoading(true);
    setUsersData([]);
    setDisplayedSlips([]);
    setBulkAdvanceData([]); // Clear previous bulk data

    const year = selectedMonth.year();
    const month = selectedMonth.month() + 1;

    try {
      // Fetch sites for attendance edit modal
      const sitesResponse = await getSites();
      console.log('Response of sites', sitesResponse);
      setSites(sitesResponse.sites || []);
      
      const response = await listUsers();
      const users = response.users || [];

      if (!users || users.length === 0) {
        messageApi.warning('No users found in the system.');
        setLoading(false);
        return;
      }

      // Fetch all required data for the month - ALL APIs must succeed
      let allSalarySlips = [];
      let allPrevMonthSlips = [];
      let allAttendanceData = [];
      let allAdvanceData = [];

      messageApi.info('Fetching salary slips, attendance, and advance data...');

      try {
        // Fetch all salary slips for current month
        const slipsResponse = await listSalarySlipsForMonth(year, month);
        allSalarySlips = slipsResponse.salarySlips || [];

        // Fetch all salary slips for previous month (for carry forward calculation)
        const currentMonthYear = `${year}-${month.toString().padStart(2, '0')}`;
        const prevMonth = dayjs(currentMonthYear).subtract(1, 'month').format('YYYY-MM');
        const prevMonthResponse = await listSalarySlipsForMonth(parseInt(prevMonth.split('-')[0]), parseInt(prevMonth.split('-')[1]));
        allPrevMonthSlips = prevMonthResponse.salarySlips || [];

        // Fetch all attendance data for the month
        const attendanceResponse = await getAllUsersAttendanceForMonth(year, month);
        allAttendanceData = attendanceResponse.attendanceData || [];

        // Fetch all advance data for the month
        const advanceResponse = await getAllUsersMonthlyAdvanceDetails(year, month);
        allAdvanceData = advanceResponse.advanceData || [];

        // Store bulk advance data in state for use in advance history popup
        setBulkAdvanceData(allAdvanceData);

        console.log(`[fetchUsers] Successfully fetched all data: ${allSalarySlips.length} salary slips, ${allPrevMonthSlips.length} prev month slips, ${allAttendanceData.length} attendance records, ${allAdvanceData.length} advance records`);

        // Validate that we have the expected data structure
        if (!Array.isArray(allSalarySlips) || !Array.isArray(allPrevMonthSlips) || 
            !Array.isArray(allAttendanceData) || !Array.isArray(allAdvanceData)) {
          throw new Error('Invalid data structure received from APIs');
        }

        messageApi.success('All data fetched successfully!');

      } catch (error) {
        console.error('Failed to fetch required data for month:', error);
        messageApi.error(`Failed to fetch required data: ${error.message}. Please try again.`);
        setLoading(false);
        return; // Exit early - don't proceed with incomplete data
      }

      const userDataPromises = users.map(async (user) => {
        let totalShiftsWorked = 0;
        let pendingAdvance = 0;
        let slipExists = false;
        let existingDeduction = 0;
        let existingProfTax = 0;
        let existingFoodAllowance = 0;
        let existingTravelAllowance = 0;
        let existingMaterialReimbursement = 0;
        let existingMaterialReimbursementNarration = '';
        let existingBonus = 0;
        let existingBonusNarration = '';

        // Find attendance data for current user and month
        const userAttendanceData = allAttendanceData.find(att => att.MobileNumber === user.MobileNumber);
        if (userAttendanceData) {
          totalShiftsWorked = (userAttendanceData.attendanceRecords || []).reduce((acc, curr) => acc + (curr.ShiftMultiplier || 0), 0);
        }

        // Find advance data for current user and month
        const userAdvanceData = allAdvanceData.find(adv => adv.MobileNumber === user.MobileNumber);
        if (userAdvanceData) {
          pendingAdvance = (userAdvanceData.carryForward || 0) + (userAdvanceData.advancesForMonth || []).reduce((acc, curr) => acc + curr.Amount, 0);
        }

        // Find existing salary slip for current user and month
        const existingSlip = allSalarySlips.find(slip => slip.PK.split('#')[1] === user.MobileNumber);
        if (existingSlip) {
          slipExists = true;
          existingDeduction = existingSlip.AdvanceDeductedThisMonth;
          existingProfTax = existingSlip.ProfessionalTax || 0;
          existingFoodAllowance = existingSlip.FoodAllowance || 0;
          existingTravelAllowance = existingSlip.TravelAllowance || 0;
          existingMaterialReimbursement = existingSlip.MaterialReimbursement || 0;
          existingMaterialReimbursementNarration = existingSlip.MaterialReimbursementNarration || '';
          existingBonus = existingSlip.Bonus || 0;
          existingBonusNarration = existingSlip.BonusNarration || '';
        }

        // Calculate carry forward amount from previous month's salary slip
        let carryForwardAmount = 0;
          const currentMonthYear = `${year}-${month.toString().padStart(2, '0')}`;
          const prevMonth = dayjs(currentMonthYear).subtract(1, 'month').format('YYYY-MM');
          
          // Get previous month's salary slip
          const prevMonthSlip = allPrevMonthSlips.find(slip => slip.PK.split('#')[1] === user.MobileNumber);
          
          if (prevMonthSlip) {
            const prevSlip = prevMonthSlip;

            console.log('DEBUG: Previous month slip for ' + user.FullName + ': ' + prevSlip.PaymentStatus);
            
            // Check if there's a carry forward amount (unpaid balance)
            if (prevSlip.PaymentStatus !== 'FULLY_PAID') {
              const previousNetSalary = prevSlip.NetSalaryPaid || 0;
              const previousPartialPaid = prevSlip.partialPaidAmount || 0;
              carryForwardAmount = Math.max(0, previousNetSalary - previousPartialPaid);
              
              if (carryForwardAmount > 0) {
                console.log(`[DEBUG] Found carry forward for ${user.FullName}: ₹${carryForwardAmount} from ${prevMonth}`);
                
                // Store carry forward info for display
                setCarryForwardInfo(prev => ({
                  ...prev,
                  [user.MobileNumber]: {
                    hasCarryForward: true,
                    carryForwardAmount: carryForwardAmount,
                    previousMonth: prevMonth,
                    previousMonthNetSalary: previousNetSalary,
                    previousMonthPartialPaid: previousPartialPaid,
                    previousMonthStatus: prevSlip.PaymentStatus
                  }
                }));
              }
            }
          }


        const userData = {
          key: user.MobileNumber,
          name: `${user.FullName} (${user.Job})`,
          mobileNumber: user.MobileNumber,
          shifts: totalShiftsWorked,
          rate: user.Rate,
          gross: totalShiftsWorked * (user.Rate || 0),
          pendingAdvance: pendingAdvance,
          advanceDeduction: slipExists ? existingDeduction : 0,
          professionalTax: slipExists ? existingProfTax : 0,
          foodAllowance: slipExists ? existingFoodAllowance : 0,
          travelAllowance: slipExists ? existingTravelAllowance : 0,
          materialReimbursement: slipExists ? existingMaterialReimbursement : 0,
          materialReimbursementNarration: slipExists ? existingMaterialReimbursementNarration : '',
          bonus: slipExists ? existingBonus : 0,
          bonusNarration: slipExists ? existingBonusNarration : '',
          netSalary: 0,
          carryForwardAmount: carryForwardAmount,
          checked: !slipExists && user.Rate,
          slipExists: slipExists,
          deductionError: false, // New property for validation state
        };
        return userData;
      });

      const resolvedUsersData = await Promise.all(userDataPromises);
      const dataWithAutoDeduction = resolvedUsersData.map(u => {
        if (u.checked) { // only for users without a generated slip
            const totalEarnings = (u.gross || 0) + (u.travelAllowance || 0) + (u.materialReimbursement || 0) + (u.bonus || 0) + (u.carryForwardAmount || 0);
            const otherDeductions = (u.professionalTax || 0) + (u.foodAllowance || 0);
            const netSalaryBeforeAdvance = totalEarnings - otherDeductions;
            const autoDeduction = Math.min(u.pendingAdvance, Math.max(0, netSalaryBeforeAdvance));
            return { ...u, advanceDeduction: autoDeduction };
        }
        return u;
      });
      setUsersData(dataWithAutoDeduction);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      messageApi.error('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDisplaySlips = async () => {
    setDisplayLoading(true);
    setUsersData([]);
    setDisplayedSlips([]);

    try {
        // Fetch sites for attendance edit modal
        const sitesResponse = await getSites();
        console.log('Response of sites', sitesResponse);
        setSites(sitesResponse.sites || []);
        
        const year = selectedMonth.year();
        const month = selectedMonth.month() + 1;
        const response = await listSalarySlipsForMonth(year, month);
        setDisplayedSlips(response.salarySlips || []);
    } catch (error) {
        console.error("Error fetching salary slips:", error);
        messageApi.error("Failed to fetch salary slips for the month.");
    } finally {
        setDisplayLoading(false);
    }
  };

  const handleAdvanceInfo = async (mobileNumber) => {
    setInfoModalVisible(true);
    setModalLoading(false); // No need to show loading since we have data
    
    try {
      // Use pre-loaded data instead of making another API call
      const userData = usersData.find(user => user.mobileNumber === mobileNumber);
      
      if (!userData) {
        messageApi.error("User data not found.");
        return;
      }

      // Find the advance data for this user from the bulk data
      const year = selectedMonth.year();
      const month = selectedMonth.month() + 1;
      const monthYear = `${year}-${month.toString().padStart(2, '0')}`;
      
      // Get advance data from the bulk API response that was already loaded
      const userAdvanceData = bulkAdvanceData.find(adv => adv.MobileNumber === mobileNumber);
      
      if (userAdvanceData && userAdvanceData.advancesForMonth) {
        const history = [
          { key: 'cf', Timestamp: 'Opening Balance (Carry Forward)', Amount: userAdvanceData.carryForward || 0, isCarryForward: true},
          ...(userAdvanceData.advancesForMonth || [])
        ];
        setAdvanceHistory(history);
      } else {
        // Fallback: if bulk data not available, show basic info from userData
        const history = [
          { key: 'cf', Timestamp: 'Opening Balance (Carry Forward)', Amount: userData.pendingAdvance || 0, isCarryForward: true}
        ];
        setAdvanceHistory(history);
        console.warn(`Bulk advance data not available for user ${mobileNumber}, using fallback data`);
      }
    } catch (error) {
      console.error("Error processing advance history:", error);
      messageApi.error("Failed to load advance history.");
    }
  };

  const handleDeductionChange = (mobileNumber, value) => {
    const newData = usersData.map(item => {
      if (item.mobileNumber === mobileNumber) {
        const totalEarnings = (item.gross || 0) + (item.travelAllowance || 0) + (item.materialReimbursement || 0) + (item.bonus || 0) + (item.carryForwardAmount || 0);
        const otherDeductions = (item.professionalTax || 0) + (item.foodAllowance || 0);
        const netSalaryBeforeAdvance = totalEarnings - otherDeductions;
        
        let deductionError = false;
        if (value > netSalaryBeforeAdvance) {
          messageApi.error('Advance deduction cannot result in a negative salary.', 2);
          deductionError = true;
        } else if (value > item.pendingAdvance) {
          messageApi.error('Advance deduction cannot exceed the pending advance amount.', 2);
          deductionError = true;
        }

        return { ...item, advanceDeduction: value, deductionError };
      }
      return item;
    });
    setUsersData(newData);
  };
  
  const handleOtherValueChange = (mobileNumber, field, value) => {
    const newData = usersData.map(item => {
      if (item.mobileNumber === mobileNumber) {
        const updatedItem = { ...item, [field]: value };
        console.log('DBUG: Carry Forward for ' + updatedItem.name + ': ' + updatedItem.carryForwardAmount);
        const totalEarnings = (updatedItem.gross || 0) + (updatedItem.travelAllowance || 0) + (updatedItem.materialReimbursement || 0) + (updatedItem.bonus || 0) + (updatedItem.carryForwardAmount || 0);
        const otherDeductions = (updatedItem.professionalTax || 0) + (updatedItem.foodAllowance || 0);
        const netSalaryBeforeAdvance = totalEarnings - otherDeductions;
        const autoDeduction = Math.min(updatedItem.pendingAdvance, Math.max(0, netSalaryBeforeAdvance));

        return { ...updatedItem, advanceDeduction: autoDeduction, deductionError: false };
      }
      return item;
    });
    setUsersData(newData);
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    const newData = usersData.map(user => 
        user.slipExists || !user.rate ? user : { ...user, checked: isChecked }
    );
    setUsersData(newData);
  };

  const handleCheckboxChange = (mobileNumber, checked) => {
    const newData = usersData.map(item => {
        if (item.mobileNumber === mobileNumber) {
            return { ...item, checked: checked };
        }
        return item;
    });
    setUsersData(newData);
  };

  const generateSlips = async () => {
    setGenerationLoading(true);
    const selectedUsers = usersData.filter(u => u.checked && !u.slipExists);

    if (selectedUsers.length === 0) {
      messageApi.warn('No users selected for salary slip generation.');
      setGenerationLoading(false);
      return;
    }

    let successCount = 0;
    let errorCount = 0;
    let validationError = false;

    for (const user of selectedUsers) {
        if(user.deductionError) {
            validationError = true;
            messageApi.error(`Cannot generate slip for ${user.name} due to an invalid advance deduction amount.`);
            continue;
        }
      try {
        await generateSalarySlip(
            user.mobileNumber,
            selectedMonth.format('YYYY-MM'),
            user.advanceDeduction || 0,
            user.professionalTax || 0,
            user.foodAllowance || 0,
            user.travelAllowance || 0,
            user.materialReimbursement || 0,
            user.materialReimbursementNarration || '',
            user.bonus || 0,
            user.bonusNarration || ''
        );
        successCount++;
        messageApi.success(`Salary slip generated for ${user.name}`);
      } catch (error) {
        errorCount++;
        messageApi.error(`Failed to generate salary slip for ${user.name}.`);
        console.error(`Error generating slip for ${user.mobileNumber}`, error.response?.data || error);
      }
    }

    setGenerationLoading(false);
    if (successCount > 0) {
      messageApi.info(`${successCount} salary slips generated successfully.`);
      handleFetchData(); // Refresh data to show generated slips
    }
    if (errorCount > 0) {
      messageApi.error(`${errorCount} salary slips failed to generate.`);
    }
  };

  const handleEditSlip = (slip) => {
    setEditingSlip(slip);
    editForm.setFieldsValue({
        advanceDeductedThisMonth: slip.AdvanceDeductedThisMonth,
        professionalTax: slip.ProfessionalTax,
        foodAllowance: slip.FoodAllowance,
        travelAllowance: slip.TravelAllowance || 0,
        materialReimbursement: slip.MaterialReimbursement || 0,
        materialReimbursementNarration: slip.MaterialReimbursementNarration || '',
        bonus: slip.Bonus || 0,
        bonusNarration: slip.BonusNarration || '',
    });
    setEditModalVisible(true);
  };

  const handleUpdateSlip = async (values) => {
    if (!editingSlip) return;
    
    // --- Validation Start ---
    const totalEarnings = (editingSlip.GrossSalary || 0) + (values.travelAllowance || 0) + (values.materialReimbursement || 0) + (values.bonus || 0);
    const otherDeductions = (values.professionalTax || 0) + (values.foodAllowance || 0);
    const netSalaryBeforeAdvance = totalEarnings - otherDeductions;

    if (values.advanceDeductedThisMonth > netSalaryBeforeAdvance) {
      messageApi.error('Advance deduction cannot result in a negative salary.');
      return;
    }

    if (values.advanceDeductedThisMonth > editingSlip.TotalPendingAdvanceBeforeDeduction) {
      messageApi.error('Advance deduction cannot exceed the total pending advance for that month.');
      return;
    }
    // --- Validation End ---

    setModalLoading(true);
    try {
        const [year, month] = editingSlip.MonthYear.split('-');
        await updateSalarySlip(editingSlip.PK.split('#')[1], year, month, values);
        messageApi.success('Salary slip updated successfully!');
        setEditModalVisible(false);
        setEditingSlip(null);
        handleDisplaySlips(); // Refresh the displayed slips
    } catch(error) {
        messageApi.error(error.response?.data?.message || 'Failed to update salary slip.');
    } finally {
        setModalLoading(false);
    }
  };

  const handleConfirmDelete = async (slip) => {
    try {
      const [year, month] = slip.MonthYear.split('-');
      await deleteSalarySlip(slip.PK.split('#')[1], year, month);
      messageApi.success('Salary slip deleted successfully!');
      handleDisplaySlips(); // Refresh the list
    } catch (error) {
      messageApi.error(error.response?.data?.message || 'Failed to delete salary slip.');
    }
  };

  // Material narration handling functions
  const handleMaterialNarration = (mobileNumber) => {
    const user = usersData.find(u => u.mobileNumber === mobileNumber);
    setEditingMaterialNarration({
      mobileNumber,
      narration: user.materialReimbursementNarration || ''
    });
    materialNarrationForm.setFieldsValue({
      narration: user.materialReimbursementNarration || ''
    });
    setMaterialNarrationModalVisible(true);
  };

  const handleSaveMaterialNarration = (values) => {
    const newData = usersData.map(item => {
      if (item.mobileNumber === editingMaterialNarration.mobileNumber) {
        return { ...item, materialReimbursementNarration: values.narration };
      }
      return item;
    });
    setUsersData(newData);
    setMaterialNarrationModalVisible(false);
    setEditingMaterialNarration(null);
    messageApi.success('Material reimbursement narration saved successfully!');
  };

  // Bonus narration handling functions
  const handleBonusNarration = (mobileNumber) => {
    const user = usersData.find(u => u.mobileNumber === mobileNumber);
    setEditingBonusNarration({
      mobileNumber,
      narration: user.bonusNarration || ''
    });
    bonusNarrationForm.setFieldsValue({
      narration: user.bonusNarration || ''
    });
    setBonusNarrationModalVisible(true);
  };

  const handleSaveBonusNarration = (values) => {
    const newData = usersData.map(item => {
      if (item.mobileNumber === editingBonusNarration.mobileNumber) {
        return { ...item, bonusNarration: values.narration };
      }
      return item;
    });
    setUsersData(newData);
    setBonusNarrationModalVisible(false);
    setEditingBonusNarration(null);
    messageApi.success('Bonus narration saved successfully!');
  };

  const handleEditAttendance = async (salarySlip) => {
    console.log('Salary slip:', salarySlip);
    
    // Extract mobile number from PK field (USER#<MobileNumber>)
    const mobileNumber = salarySlip.PK.replace('USER#', '');
    
    setAttendanceLoading(true);
    setAttendanceEditModalVisible(true);
    
    // Reset form and data
    attendanceEditForm.resetFields();
    setAttendanceData({});
    
    try {
      // Fetch user profile to get employment type and name
      const userResponse = await listUsers();
      const user = userResponse.users.find(u => u.MobileNumber === mobileNumber);
      
      if (!user) {
        messageApi.error('User profile not found');
        return;
      }
      
      setEditingAttendance({
        mobileNumber: mobileNumber,
        name: user.FullName,
        employmentType: user.EmploymentType || 'SHIFT'
      });
      
      // Fetch attendance data for the selected month for this specific user
      const year = selectedMonth.year();
      const month = selectedMonth.month() + 1;
      const response = await getAttendanceForMonth(mobileNumber, year, month);
      console.log('Attendance response:', response);
      
      if (response && response.attendanceRecords) {
        // Convert attendance data to form format
        const attendanceFormData = {};
        for (let day = 1; day <= selectedMonth.daysInMonth(); day++) {
          const date = selectedMonth.date(day).format('YYYY-MM-DD');
          const attendanceRecord = response.attendanceRecords.find(record => record.Date === date);
          console.log('Attendance record for day', day, ':', attendanceRecord);
          if (attendanceRecord) {
            attendanceFormData[`day_${day}`] = {
              status: attendanceRecord.Status,
              extraShifts: attendanceRecord.ExtraShifts || 0,
              siteId: attendanceRecord.SiteId || null
            };
          } else {
            attendanceFormData[`day_${day}`] = {
              status: '',
              extraShifts: 0,
              siteId: null
            };
          }
        }
        console.log('Attendance form data:', attendanceFormData);
        setAttendanceData(attendanceFormData);
      }
    } catch (error) {
      console.error('Error in handleEditAttendance:', error);
      messageApi.error('Failed to fetch attendance data');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleSaveAttendanceEdit = async (values) => {
    try {
      setAttendanceLoading(true);
      
      // Convert form values back to attendance format
      const attendanceUpdates = {};
      for (let day = 1; day <= selectedMonth.daysInMonth(); day++) {
        const dayKey = `day_${day}`;
        const dayData = values[dayKey];
        if (dayData && dayData.status && dayData.status !== '') {
          // Use day number as key, not full date
          attendanceUpdates[day] = {
            status: dayData.status,
            extraShifts: dayData.extraShifts || 0,
            siteId: dayData.siteId || null
          };
        }
      }

      // Call backend to update attendance (this will also recalculate salary if slip exists)
      const year = selectedMonth.year();
      const month = selectedMonth.month() + 1;
      const response = await updateMonthlyAttendance(editingAttendance.mobileNumber, year, month, attendanceUpdates);
      
      if (response.salaryRecalculated) {
        messageApi.success('Attendance updated and salary recalculated successfully');
        // Refresh displayed slips to show updated salary
        await handleDisplaySlips();
      } else {
        messageApi.success('Attendance updated successfully');
      }
      setAttendanceEditModalVisible(false);
      setEditingAttendance(null);
      attendanceEditForm.resetFields();
      
    } catch (error) {
      messageApi.error('Failed to update attendance');
    } finally {
      setAttendanceLoading(false);
    }
  };
  console.log('DEBUG: Users Data: ', usersData);
  console.log('DEBUG: Site Data', sites);
  const eligibleForSelection = usersData.filter(u => !u.slipExists && u.rate);
  const usersWithoutRate = usersData.filter(u => !u.slipExists && !u.rate);
  const selectedCount = eligibleForSelection.filter(u => u.checked).length;
  const isAllSelected = eligibleForSelection.length > 0 && selectedCount === eligibleForSelection.length;
  const isIndeterminate = selectedCount > 0 && selectedCount < eligibleForSelection.length;

  const columns = [
    {
        title: (
            <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={handleSelectAll}
            />
        ),
        key: 'select',
        fixed: 'left',
        width: 60,
        render: (_, record) => (
          <>
            <Checkbox
                checked={record.checked}
                disabled={record.slipExists || !record.rate}
                onChange={(e) => handleCheckboxChange(record.mobileNumber, e.target.checked)}
            />
          </>
        ),
    },
    { title: 'Name', dataIndex: 'name', key: 'name', fixed: 'left', width: 200 },
    { title: 'Total Shifts', dataIndex: 'shifts', key: 'shifts' },
    { 
      title: 'Rate/Day', 
      dataIndex: 'rate', 
      key: 'rate', 
      render: (rate, record) => (rate !== undefined && rate !== null && rate !== '') ? `₹${rate}` : 'NA' 
    },
    { 
      title: 'Gross Salary', 
      dataIndex: 'gross', 
      key: 'gross', 
      render: (gross, record) => {
        const carryForwardAmount = record.carryForwardAmount || 0;
        const totalGross = gross + carryForwardAmount;
        
        if (carryForwardAmount > 0) {
          return (
            <div>
              <div>₹{totalGross.toFixed(2)}</div>
              <Tooltip title={`Current Month: ₹${gross.toFixed(2)} + Carried Forward: ₹${carryForwardAmount.toFixed(2)}`}>
                <InfoCircleOutlined style={{ color: themeConfig.status.info, marginLeft: 4 }} />
              </Tooltip>
            </div>
          );
        }
        
        return `₹${gross.toFixed(2)}`;
      }
    },
    {
      title: 'Pending Advance',
      dataIndex: 'pendingAdvance',
      key: 'pendingAdvance',
      render: (pending, record) => (
        <span>
          {`₹${pending}`}{' '}
          <Tooltip title="View month's advance history">
            <InfoCircleOutlined onClick={() => handleAdvanceInfo(record.mobileNumber)} style={{ cursor: 'pointer' }} />
          </Tooltip>
        </span>
      ),
    },
    {
      title: 'Prof. Tax',
      dataIndex: 'professionalTax',
      key: 'professionalTax',
      render: (tax, record) => (
        <InputNumber
            prefix="₹"
            value={tax}
            disabled={record.slipExists}
            onChange={(value) => handleOtherValueChange(record.mobileNumber, 'professionalTax', value)}
        />
      ),
    },
    {
      title: 'Food Allowance',
      dataIndex: 'foodAllowance',
      key: 'foodAllowance',
      render: (allowance, record) => (
        <InputNumber
            prefix="₹"
            value={allowance}
            disabled={record.slipExists}
            onChange={(value) => handleOtherValueChange(record.mobileNumber, 'foodAllowance', value)}
        />
      ),
    },
    {
      title: 'Travel Allowance',
      dataIndex: 'travelAllowance',
      key: 'travelAllowance',
      render: (allowance, record) => (
        <InputNumber
            prefix="₹"
            value={allowance}
            disabled={record.slipExists}
            onChange={(value) => handleOtherValueChange(record.mobileNumber, 'travelAllowance', value)}
        />
      ),
    },
    {
      title: 'Material Reimbursement',
      dataIndex: 'materialReimbursement',
      key: 'materialReimbursement',
      render: (reimbursement, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <InputNumber
              prefix="₹"
              value={reimbursement}
              disabled={record.slipExists}
              onChange={(value) => handleOtherValueChange(record.mobileNumber, 'materialReimbursement', value)}
          />
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleMaterialNarration(record.mobileNumber)}
            disabled={record.slipExists}
            title="Add/Edit Narration"
          />
        </div>
      ),
    },
    {
      title: 'Bonus',
      dataIndex: 'bonus',
      key: 'bonus',
      render: (bonus, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <InputNumber
              prefix="₹"
              value={bonus}
              disabled={record.slipExists}
              onChange={(value) => handleOtherValueChange(record.mobileNumber, 'bonus', value)}
          />
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleBonusNarration(record.mobileNumber)}
            disabled={record.slipExists}
            title="Add/Edit Narration"
          />
        </div>
      ),
    },
    {
      title: 'Advance Deduction',
      dataIndex: 'advanceDeduction',
      key: 'advanceDeduction',
      render: (deduction, record) => (
        <InputNumber
          prefix="₹"
          value={deduction}
          status={record.deductionError ? 'error' : ''}
          disabled={record.slipExists}
          onChange={(value) => handleDeductionChange(record.mobileNumber, value)}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) =>
        record.slipExists ? <Tag color="green">Generated</Tag> : <Tag color="blue">Pending</Tag>,
    },
  ];

  const displayColumns = [
    {
      title: 'Employee & Period',
      key: 'employee',
      render: (_, record) => (
        <div>
          <strong>{record.FullName}</strong> ({record.Job})
          <br />
          <Tag color={record.EmploymentType === 'FIXED' ? 'green' : 'blue'}>{record.EmploymentType}</Tag>
          <br />
          <Tag>{record.MonthYear}</Tag>
        </div>
      ),
    },
    {
      title: 'Salary Details',
      key: 'salary',
      render: (_, record) => (
        <div>
          <div>Gross: ₹{record.GrossSalary.toFixed(2)}</div>
          <div><Tag color="blue">Additions: ₹{((record.TravelAllowance || 0) + (record.MaterialReimbursement || 0) + (record.Bonus || 0)).toFixed(2)}</Tag></div>
          <div><Tag color="orange">Total Earnings: ₹{(record.TotalEarnings || record.GrossSalary).toFixed(2)}</Tag></div>
          {record.BalanceCarriedForward > 0 && (
            <div>
              <Tag color="purple">
                Carried Forward: ₹{record.BalanceCarriedForward.toFixed(2)}
                <Tooltip title={`Balance carried forward from previous month`}>
                  <InfoCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </Tag>
            </div>
          )}
          <div><Tag color="green">Net Paid: ₹{record.NetSalaryPaid.toFixed(2)}</Tag></div>
          <small>
            ({record.TotalShiftsWorked} {record.EmploymentType === 'FIXED' ? 'days' : 'shifts'} @ ₹{record.ApplicableRate}/{record.EmploymentType === 'FIXED' ? 'day' : 'shift'})
          </small>
        </div>
      ),
    },
    {
      title: 'Deductions from Gross',
      key: 'deductions',
      render: (_, record) => (
        <div>
            <div>Advance: - ₹{record.AdvanceDeductedThisMonth.toFixed(2)}</div>
            <div>Prof. Tax: - ₹{(record.ProfessionalTax || 0).toFixed(2)}</div>
            <div>Food: - ₹{(record.FoodAllowance || 0).toFixed(2)}</div>
        </div>
      ),
    },
    {
      title: 'Material Reimbursement',
      key: 'materialReimbursement',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>₹{(record.MaterialReimbursement || 0).toFixed(2)}</span>
          {record.MaterialReimbursementNarration && (
            <Tooltip title={record.MaterialReimbursementNarration}>
              <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Bonus',
      key: 'bonus',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>₹{(record.Bonus || 0).toFixed(2)}</span>
          {record.BonusNarration && (
            <Tooltip title={record.BonusNarration}>
              <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      render: (_, record) => {
        let color = 'default';
        let text = 'Unknown';
        
        if (record.PaymentStatus === 'PAID') {
          color = 'success';
          text = 'Fully Paid';
        } else if (record.PaymentStatus === 'PARTIAL_PAID') {
          color = 'warning';
          text = 'Partially Paid';
        } else if (record.PaymentStatus === 'PENDING') {
          color = 'processing';
          text = 'Pending';
        } else if (record.PaymentStatus === 'CARRIED_FORWARD') {
          color = 'default';
          text = 'Carried Forward';
        } else if (record.PaymentStatus === 'AUTO') {
          color = 'info';
          text = 'Auto Generated';
        } else if (record.PaymentStatus === 'ADVANCE') {
          color = 'success';
          text = 'Adjusted Against Advance';
        }
        
        return (
          <div>
            <Tag color={color}>{text}</Tag>
            {record.PaymentStatus === 'PARTIAL_PAID' && record.partialPaidAmount && (
              <div style={{marginTop: '4px', fontSize: '11px', color: themeConfig.text.muted }}>
                Paid: ₹{record.partialPaidAmount.toFixed(2)}
              </div>
            )}
            {record.PaymentStatus === 'CARRIED_FORWARD' && (
              <div style={{marginTop: '4px', fontSize: '11px', color: themeConfig.text.muted }}>
                Balance carried to next month
              </div>
            )}
            {record.PaymentStatus === 'ADVANCE' && (
              <div style={{marginTop: '4px', fontSize: '11px', color: themeConfig.text.muted }}>
                Salary adjusted against advance
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Advance Account Summary',
      key: 'advance',
      render: (_, record) => (
        <div>
            <div>Opening Balance: ₹{record.OpeningAdvance.toFixed(2)}</div>
            <div>Added this month: + ₹{record.AdvancesThisMonth.toFixed(2)}</div>
            <hr style={{margin: '2px 0', borderStyle: 'dashed', borderColor: themeConfig.border.muted }} />
            <div><strong>Total Pending:</strong> ₹{record.TotalPendingAdvanceBeforeDeduction.toFixed(2)}</div>
            <div>Closing Balance (C/F): ₹{record.AdvanceCarriedForward.toFixed(2)}</div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div>
          <Space>
            <Tooltip title="Edit Slip Details">
                <Button icon={<EditOutlined />} onClick={() => handleEditSlip(record)}>
                  Edit
                </Button>
            </Tooltip>
            <Tooltip 
              title={record.PaymentStatus === 'PAID' || record.PaymentStatus === 'PARTIAL_PAID' || record.PaymentStatus === 'ADVANCE' || record.PaymentStatus === 'AUTO'
                ? record.PaymentStatus === 'ADVANCE' 
                  ? "Cannot delete: Salary slip is adjusted against advance"
                  : record.PaymentStatus === 'AUTO'
                  ? "Cannot delete: Auto-generated salary slip"
                  : "Cannot delete: Payment has been recorded against this salary slip"
                : "Delete this salary slip"
              }
            >
              <Popconfirm
                title="Are you sure you want to delete this salary slip?"
                description={`This will permanently delete the slip for ${record.FullName} for ${record.MonthYear}.`}
                onConfirm={() => handleConfirmDelete(record)}
                okText="Yes, Delete"
                cancelText="No"
                okType="danger"
                disabled={record.PaymentStatus === 'PAID' || record.PaymentStatus === 'PARTIAL_PAID' || record.PaymentStatus === 'ADVANCE' || record.PaymentStatus === 'AUTO'}
              >
                <Button 
                  icon={<DeleteOutlined />} 
                  danger 
                  disabled={record.PaymentStatus === 'PAID' || record.PaymentStatus === 'PARTIAL_PAID' || record.PaymentStatus === 'ADVANCE' || record.PaymentStatus === 'AUTO'}
                >
                  Delete
                </Button>
              </Popconfirm>
            </Tooltip>
          </Space>
          
          <div style={{marginTop: '8px', fontSize: '12px', color: themeConfig.text.mutedMedium }}>
            Generated: {dayjs(record.GeneratedAt).format('YYYY-MM-DD HH:mm')}
          </div>
          
          {(record.PaymentStatus === 'PAID' || record.PaymentStatus === 'PARTIAL_PAID') && (
            <div style={{marginTop: '4px', fontSize: '11px', color: themeConfig.status.error }}>
              ⚠️ Delete disabled - Payment recorded
            </div>
          )}
        </div>
      ),
    }
  ];

  const handleMonthChange = (value) => {
    const [year, month] = value.split('-');
    setSelectedMonth(dayjs().year(parseInt(year)).month(parseInt(month) - 1).startOf('month'));
  };

  // Generate month options for the last 2 years and current year
  const generateMonthOptions = () => {
    const options = [];
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month();
    
    // Add last 2 years
    for (let year = currentYear - 2; year <= currentYear; year++) {
      for (let month = 0; month < 12; month++) {
        const date = dayjs().year(year).month(month).startOf('month');
        if (date.isSameOrBefore(dayjs().endOf('month'))) {
          options.push({
            value: `${year}-${month + 1}`,
            label: date.format('MMMM YYYY')
          });
        }
      }
    }
    return options.reverse(); // Most recent first
  };

  // Filter and sort displayed slips based on search text
  const filteredDisplayedSlips = displayedSlips
    .filter(slip => {
      if (!searchText) return true;
      
      const searchLower = searchText.toLowerCase();
      const fullName = (slip.FullName || '').toLowerCase();
      const job = (slip.Job || '').toLowerCase();
      const mobileNumber = (slip.PK || '').toLowerCase();
      
      return fullName.includes(searchLower) || 
             job.includes(searchLower) || 
             mobileNumber.includes(searchLower);
    })
    .sort((a, b) => {
      const nameA = (a.FullName || '').toLowerCase();
      const nameB = (b.FullName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  return (
    <div className="salary-slip-management">
      {contextHolder}
      <h2>Salary Slip Management</h2>
      <div className="controls">
        <Space>
            <Select
                placeholder="Select Month"
                style={{ width: 200 }}
                value={`${selectedMonth.year()}-${selectedMonth.month() + 1}`}
                onChange={handleMonthChange}
                options={generateMonthOptions()}
            />
            <Button type="primary" onClick={handleFetchData} loading={loading}>
            Load Generation Data
            </Button>
            <Button onClick={handleDisplaySlips} loading={displayLoading}>
            Display All Slips for Month
            </Button>
        </Space>
      </div>

      {(loading || displayLoading) && <Spin size="large" className="table-spin" />}
      
      {!loading && eligibleForSelection.length === 0 && usersData.length > 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: themeConfig.text.muted }}>
          <InfoCircleOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
          <div>All salary slips for this month have been generated</div>
        </div>
      )}
      
      {eligibleForSelection.length > 0 && !loading && (
        <>
        <Table
            dataSource={eligibleForSelection}
            columns={columns}
            pagination={false}
            rowKey={(record) => record.mobileNumber}
            rowClassName={(record) => record.slipExists || !record.rate ? 'slip-exists-row' : ''}
            scroll={{ x: 1500, y: 500 }}
        />
        <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Button
                type="primary"
                danger
                onClick={generateSlips}
                loading={generationLoading}
                disabled={
                    eligibleForSelection.filter(u => u.checked).length === 0 ||
                    eligibleForSelection.some(u => u.checked && u.deductionError)
                }
            >
                Generate Slips for Selected ({eligibleForSelection.filter(u => u.checked).length})
            </Button>
        </div>
        
        {usersWithoutRate.length > 0 && (
          <div style={{ marginTop: 16, padding: '12px 16px', backgroundColor: themeConfig.status.warningBg, border: `1px solid ${themeConfig.status.warningBorder}`, borderRadius: '6px' }}>
            <InfoCircleOutlined style={{ color: themeConfig.status.warningLight, marginRight: 8 }} />
            <span style={{ color: themeConfig.status.warningDark }}>
              <strong>Note:</strong> {usersWithoutRate.length} user(s) cannot generate salary slips because they don't have a rate configured. 
              Please set a rate for these users in User Management first.
            </span>
          </div>
        )}

        {/* Carry Forward Information */}
        {Object.keys(carryForwardInfo).length > 0 && (
          <div style={{ marginTop: 16, padding: '12px 16px', backgroundColor: themeConfig.status.successBg, border: `1px solid ${themeConfig.status.successBorder}`, borderRadius: '6px' }}>
            <InfoCircleOutlined style={{ color: themeConfig.status.success, marginRight: 8 }} />
            <span style={{ color: themeConfig.status.successLight }}>
              <strong>Carry Forward Information:</strong> The following users have pending balances from previous months that will be automatically carried forward:
            </span>
            <div style={{ marginTop: 8 }}>
              {Object.entries(carryForwardInfo).map(([mobileNumber, info]) => {
                const user = usersData.find(u => u.mobileNumber === mobileNumber);
                return (
                  <div key={mobileNumber} style={{ marginLeft: 16, fontSize: '12px' }}>
                    • <strong>{user?.name || mobileNumber}</strong>: ₹{info.carryForwardAmount.toFixed(2)} from {info.previousMonth}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </>
      )}

      {displayedSlips.length > 0 && !displayLoading && (
        <>
            <h3 style={{marginTop: '2rem'}}>Generated Slips for {selectedMonth.format('MMMM YYYY')}</h3>
            <div style={{ marginBottom: 16 }}>
              <Input.Search
                placeholder="Search by employee name, job, or mobile number..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 400 }}
                allowClear
              />
              {searchText && (
                <span style={{ marginLeft: 8, color: themeConfig.text.muted }}>
                  Showing {filteredDisplayedSlips.length} of {displayedSlips.length} slips
                </span>
              )}
            </div>
            <Table
                dataSource={filteredDisplayedSlips}
                columns={displayColumns}
                pagination={false}
                rowKey={(record) => `${record.PK}-${record.SK}`}
                scroll={{ x: 'max-content' }}
            />
        </>
      )}


      <Modal
        title="Advance History"
        open={infoModalVisible}
        onCancel={() => setInfoModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setInfoModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        {modalLoading ? <Spin /> : <Table 
          dataSource={advanceHistory} 
          columns={[
            {
              title: 'Date', 
              dataIndex: 'Timestamp',
              render: (timestamp) => {
                // If it's a carry forward entry (string), show as-is
                if (typeof timestamp === 'string' && timestamp.includes('Opening Balance')) {
                  return timestamp;
                }
                // If it's a valid date, format it
                if (timestamp) {
                  try {
                    return dayjs(timestamp).format('DD MMM YYYY');
                  } catch (e) {
                    return timestamp;
                  }
                }
                return '-';
              }
            }, 
            {
              title: 'Amount', 
              dataIndex: 'Amount', 
              render: (amt) => `₹${(amt || 0).toLocaleString()}`
            },
            {
              title: 'Narration', 
              dataIndex: 'Notes', 
              render: (notes) => notes || '-'
            }
          ]} 
          pagination={false} 
          rowKey="key" 
        />}
      </Modal>

      <Modal
            title="Edit Salary Slip"
            visible={editModalVisible}
            onCancel={() => setEditModalVisible(false)}
            footer={null}
            destroyOnClose
        >
            <Spin spinning={modalLoading}>
                <Form form={editForm} layout="vertical" onFinish={handleUpdateSlip}>
                    <Form.Item
                        name="advanceDeductedThisMonth"
                        label="Advance Deduction"
                        rules={[{ required: true }]}
                    >
                        <InputNumber prefix="₹" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="professionalTax"
                        label="Professional Tax"
                        rules={[{ required: true }]}
                    >
                        <InputNumber prefix="₹" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="foodAllowance"
                        label="Food Allowance"
                        rules={[{ required: true }]}
                    >
                        <InputNumber prefix="₹" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="travelAllowance"
                        label="Travel Allowance"
                    >
                        <InputNumber prefix="₹" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="materialReimbursement"
                        label="Material Reimbursement"
                    >
                        <InputNumber prefix="₹" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="materialReimbursementNarration"
                        label="Material Reimbursement Narration"
                    >
                        <Input.TextArea rows={3} placeholder="Enter narration for material reimbursement" />
                    </Form.Item>
                    <Form.Item
                        name="bonus"
                        label="Bonus"
                    >
                        <InputNumber prefix="₹" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        name="bonusNarration"
                        label="Bonus Narration"
                    >
                        <Input.TextArea rows={3} placeholder="Enter narration for bonus" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                Update Salary Slip
                            </Button>
                            <Button 
                                type="default" 
                                onClick={() => handleEditAttendance(editingSlip)}
                                disabled={!editingSlip}
                            >
                                Edit Attendance
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>

      {/* Material Narration Modal */}
      <Modal
        title="Material Reimbursement Narration"
        open={materialNarrationModalVisible}
        onCancel={() => setMaterialNarrationModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={materialNarrationForm} layout="vertical" onFinish={handleSaveMaterialNarration}>
          <Form.Item
            name="narration"
            label="Narration"
            rules={[{ required: true, message: 'Please enter narration for material reimbursement' }]}
          >
            <Input.TextArea 
              rows={4} 
              placeholder="Enter detailed description of materials reimbursed (e.g., tools, equipment, supplies, etc.)"
              maxLength={500}
              showCount
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Save Narration
              </Button>
              <Button onClick={() => setMaterialNarrationModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Bonus Narration Modal */}
      <Modal
        title="Bonus Narration"
        open={bonusNarrationModalVisible}
        onCancel={() => setBonusNarrationModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={bonusNarrationForm} layout="vertical" onFinish={handleSaveBonusNarration}>
          <Form.Item
            name="narration"
            label="Narration"
            rules={[{ required: true, message: 'Please enter narration for bonus' }]}
          >
            <Input.TextArea 
              rows={4} 
              placeholder="Enter detailed description of bonus (e.g., performance bonus, festival bonus, incentive, etc.)"
              maxLength={500}
              showCount
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Save Narration
              </Button>
              <Button onClick={() => setBonusNarrationModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Attendance Edit Modal */}
      <Modal
        title={`Edit Attendance - ${editingAttendance?.name || ''}`}
        open={attendanceEditModalVisible}
        onCancel={() => setAttendanceEditModalVisible(false)}
        footer={null}
        destroyOnClose
        width={800}
      >
        <Spin spinning={attendanceLoading}>
          <Form 
            form={attendanceEditForm} 
            layout="vertical" 
            onFinish={handleSaveAttendanceEdit}
            key={editingAttendance?.mobileNumber || 'default'}
          >
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {Array.from({ length: selectedMonth.daysInMonth() }, (_, i) => i + 1).map(day => (
                <div key={day} style={{ marginBottom: '16px', padding: '8px', border: `1px solid ${themeConfig.background.container}`, borderRadius: '4px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Day {day} - {selectedMonth.date(day).format('dddd, DD MMM YYYY')}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Form.Item
                      name={[`day_${day}`, 'status']}
                      label="Status"
                      style={{ margin: 0, minWidth: '120px' }}
                    >
                      <Select placeholder="Select status" allowClear>
                        <Select.Option value="">Not Marked</Select.Option>
                        <Select.Option value="A">A</Select.Option>
                        <Select.Option value="P/2">P/2</Select.Option>
                        <Select.Option value="P">P</Select.Option>
                        <Select.Option value="P1/4">P1/4</Select.Option>
                        <Select.Option value="P1/2">P1/2</Select.Option>
                        <Select.Option value="PP">PP</Select.Option>
                        <Select.Option value="PP1/2">PP1/2</Select.Option>
                        <Select.Option value="PPP">PPP</Select.Option>
                        <Select.Option value="PPP1/2">PPP1/2</Select.Option>
                        <Select.Option value="PPPP">PPPP</Select.Option>
                      </Select>
                    </Form.Item>
                    {editingAttendance?.employmentType === 'FIXED' && (
                      <Form.Item
                        name={[`day_${day}`, 'extraShifts']}
                        label="Extra Shifts"
                        style={{ margin: 0, minWidth: '100px' }}
                      >
                        <InputNumber min={0} max={5} placeholder="0" />
                      </Form.Item>
                    )}
                    <Form.Item
                      name={[`day_${day}`, 'siteId']}
                      label="Site"
                      style={{ margin: 0, minWidth: '150px' }}
                    >
                      <Select placeholder="Select site" allowClear>
                        {sites.map(site => (
                          <Select.Option key={site.id} value={site.id}>
                            {site.siteName}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                </div>
              ))}
            </div>
            <Form.Item style={{ marginTop: '16px' }}>
              <Space>
                <Button type="primary" htmlType="submit" loading={attendanceLoading}>
                  Save Attendance
                </Button>
                <Button onClick={() => setAttendanceEditModalVisible(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
};

export default SalarySlipManagement; 